import Vue from 'vue';
import Router from 'vue-router';

// Core Pages
import DashboardLayout from '../views/Main/Layout.vue';
import Dashboard from '../views/Main/Dashboard.vue';
import Profile from '../views/Main/Profile.vue'
import Videos from '../views/Main/Videos.vue'
import Documents from '../views/Main/Documents.vue'

// User Management Pages
const Users = () => import('../views/Main/Users.vue') 
const EditUser = () => import('../views/Main/EditUser.vue') 

// Content Management Pages
const VideoManagement = () => import('../views/Main/VideoManagement.vue') 
const EditVideo = () => import('../views/Main/ContentMgmt/EditVideo.vue') 
const AddVideo = () => import('../views/Main/ContentMgmt/AddVideo.vue') 

const DocumentsManagement = () => import('../views/Main/DocumentsManagement.vue') 
const AddDocument = () => import('../views/Main/ContentMgmt/AddDocument.vue')
const EditDocument = () => import('../views/Main/ContentMgmt/EditDocument.vue') 

// Authentication pages
import Authentication from '../views/Main/Authentication.vue'
import Login from '../views/Main/Auth/Login.vue'

// Payment Pages
const Payment = () => import('../views/Main/Payment.vue') 
const Checkout = () => import('../views/Main/Payment/Checkout.vue') 
const Failed = () => import('../views/Main/Payment/Failed.vue') 
const Success = () => import('../views/Main/Payment/Success.vue') 
const Pending = () => import('../views/Main/Payment/Pending.vue') 

// General Information Pages
const Information = () => import('@/views/Main/Information.vue')
const Privacy = () => import('@/views/Main/Info/Privacy.vue')
const Deleted = () => import('@/views/Main/Info/Deleted.vue')
const Error = () => import('@/views/Main/Info/Error.vue')
const FAQ = () => import('@/views/Main/Info/FAQ.vue')

const GlobalConfig = () => import('@/views/Main/GlobalConfig.vue')

import firebase from 'firebase/app'
import 'firebase/auth'
import { mainStorage } from '@/store/main.js'

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Home',
      redirect: '/dashboard',
      component: DashboardLayout,
      beforeEnter: requireAuth,
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: Dashboard
        },
        {
          path: 'users',
          name: 'Users',
          component: Users,
          beforeEnter: requireElevatedAccess
        },
        {
          path: 'users/edit-user/:user_id',
          name: 'EditUser',
          component: EditUser,
          beforeEnter: requireElevatedAccess
        },
        {
          path: 'profile/:user_id',
          name: 'Profile',
          component: Profile
        },
        {
          path: 'videos/:cat',
          name: 'Videos',
          component: Videos,
          beforeEnter: requireVideoAccess
        },
        {
          path: 'documents/:cat',
          name: 'Documents',
          component: Documents,
          beforeEnter: requireVideoAccess
        },
        {
          path: 'video-management',
          name: 'VideoManagement',
          component: VideoManagement,
          beforeEnter: requireElevatedAccess
        },
        {
          path: 'video-management/edit-video/:video_id',
          name: 'EditVideo',
          component: EditVideo,
          beforeEnter: requireElevatedAccess
        },
        {
          path: 'video-management/add-video',
          name: 'AddVideo',
          component: AddVideo,
          beforeEnter: requireElevatedAccess
        },
        {
          path: 'documents-management',
          name: 'DocumentsManagement',
          component: DocumentsManagement,
          beforeEnter: requireElevatedAccess
        },
        {
          path: 'documents-management/add-document-plan',
          name: 'AddDocument',
          component: AddDocument,
          beforeEnter: requireElevatedAccess
        },
        {
          path: 'documents-management/edit-document/:document_id',
          name: 'EditDocument',
          component: EditDocument,
          beforeEnter: requireElevatedAccess
        },
        {
          path: 'global-configuration',
          name: 'GlobalConfig',
          component: GlobalConfig,
          beforeEnter: requireElevatedAccess
        }
      ]
    },
    {
      path: '/authentication',
      name: 'Authentication',
      redirect: '/login',
      component: Authentication,
      meta: {
        noBody: true
      },
      beforeEnter: requireUnAuth,
      children: [
        {
          path: '/login',
          name: 'Login',
          component: Login
        }
      ]
    },
    {
      path: '/payment',
      name: 'Payment',
      redirect: '/checkout',
      component: Payment,
      beforeEnter: requireAuth,
      children: [
        {
          path: '/checkout',
          name: 'Checkout',
          component: Checkout,
        },
        {
          path: '/checkout/failed',
          name: 'Failed',
          component: Failed
        },
        {
          path: '/checkout/success/:session_id',
          name: 'Success',
          component: Success
        },
        {
          path: '/checkout/pending',
          name: 'Pending',
          component: Pending
        }
      ]
    },
    {
      path: '/info',
      name: 'Info',
      redirect: '/privacy',
      component: Information,
      children: [
        {
          path: '/privacy',
          name: 'Privacy',
          component: Privacy
        },
        {
          path: '/deleted',
          name: 'Deleted',
          component: Deleted
        },
        {
          path: '/error',
          name: 'Error',
          component: Error
        },
        {
          path: '/faq',
          name: 'FAQ',
          component: FAQ
        }
      ]
    }
  ],
  scrollBehavior: (to, from ,savedPosition) => {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  }
})

function requireAuth(to, from, next) {
  // Check if the user has logged in
  let user = firebase.auth().currentUser
  if (user) {
    next()
  } else {
    next({ name: 'Login' })
  }
}

function requireUnAuth(to, from, next) {
  // Check if the user has logged in
  let user = firebase.auth().currentUser
  if (user) {
    next({ name: 'Home' })
  } else {
    next()
  }
}

function requireVideoAccess(to, from, next) {
  // Check if the user has logged in
  let user = firebase.auth().currentUser
  if (user) {
    // Check if the user can access the videos or if it has elevated credentials
    // let videoAccess = localStorage.getItem('user-video-access')
    let videoAccess = mainStorage.state.user.cHasVideoAccess
    if (videoAccess) {
      next()
    } else {
      next({ name: 'Home' })
    }
  } else {
    next({ name: 'Login' })
  }
}

function requireElevatedAccess(to, from, next) {
  // Check if the user has logged in
  let user = firebase.auth().currentUser
  if (user) {
    // Check if the user has elevated access
    // let access = localStorage.getItem('user-type')
    let elevatedAccess = mainStorage.state.user.cHasElevatedAccess
    if (elevatedAccess) {
      next()
    } else {
      next({ name: 'Home' })
    }
  }
  else {
    next({ name: 'Login' })
  }
}

export default router
